import React, { useState, useEffect, useRef } from 'react';

const LoadingScreen = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('Initializing systems');
  const [fadeOut, setFadeOut] = useState(false);
  const canvasRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  const progressRef = useRef(0);
  const statusRef = useRef('Initializing systems');
  onCompleteRef.current = onComplete;

  const statuses = [
    'Initializing systems',
    'Connecting to markets',
    'Loading trading engine',
    'Calibrating AI models',
    'Analyzing market data',
    'Preparing dashboard',
  ];

  
  useEffect(() => {
    let target = 0;
    let current = 0;
    let done = false;
    let animId;
    let lastRendered = -1;

    const stepInterval = setInterval(() => {
      target += Math.random() * 18 + 8;
      if (target >= 100) { target = 100; clearInterval(stepInterval); }
    }, 120);

    const tick = () => {
      current += (target - current) * 0.14;
      if (target >= 100 && current > 99.5) current = 100;
      progressRef.current = current;

      const idx = Math.min(Math.floor(current / 17), 5);
      statusRef.current = statuses[idx];

      
      const rounded = Math.floor(current);
      if (rounded !== lastRendered) {
        lastRendered = rounded;
        setProgress(rounded);
        setStatus(statuses[idx]);
      }

      if (current >= 100 && !done) {
        done = true;
        setTimeout(() => {
          setFadeOut(true);
          setTimeout(() => onCompleteRef.current(), 700);
        }, 300);
      }
      if (!done) animId = requestAnimationFrame(tick);
    };
    animId = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(animId); clearInterval(stepInterval); };
  }, []);

  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    let w, h;
    const resize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
    };
    resize();
    window.addEventListener('resize', resize);

    
    const candleCount = 50;
    const candles = [];
    let price = 44000 + Math.random() * 4000;
    for (let i = 0; i < candleCount; i++) {
      const change = (Math.random() - 0.48) * 800;
      const open = price;
      const close = price + change;
      const high = Math.max(open, close) + Math.random() * 400;
      const low = Math.min(open, close) - Math.random() * 400;
      candles.push({ open, close, high, low, bullish: close > open });
      price = close;
    }

    
    const volumes = candles.map(() => Math.random() * 0.6 + 0.2);

    
    const particles = [];
    for (let i = 0; i < 20; i++) {
      particles.push({
        x: Math.random() * 2000,
        y: Math.random() * 1200,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.3 - 0.15,
        size: Math.random() * 1.5 + 0.5,
        phase: Math.random() * Math.PI * 2,
      });
    }

    let animId;
    let revealIndex = 0;
    let time = 0;

    const draw = () => {
      time += 0.02;
      if (revealIndex < candleCount) revealIndex += 0.6;
      const shown = Math.floor(revealIndex);

      
      ctx.clearRect(0, 0, w, h);

      if (shown === 0) { animId = requestAnimationFrame(draw); return; }

      
      let allHigh = -Infinity, allLow = Infinity;
      for (let i = 0; i < shown; i++) {
        if (candles[i].high > allHigh) allHigh = candles[i].high;
        if (candles[i].low < allLow) allLow = candles[i].low;
      }
      const range = allHigh - allLow || 1;

      const chartLeft = w * 0.08;
      const chartRight = w * 0.92;
      const chartTop = h * 0.15;
      const chartBottom = h * 0.72;
      const chartW = chartRight - chartLeft;
      const chartH = chartBottom - chartTop;
      const gap = chartW / candleCount;
      const candleW = gap * 0.65;

      const toY = (val) => chartTop + (1 - (val - allLow) / range) * chartH;

      
      ctx.beginPath();
      for (let i = 0; i <= 5; i++) {
        const y = chartTop + (chartH / 5) * i;
        ctx.moveTo(chartLeft, y);
        ctx.lineTo(chartRight, y);
      }
      for (let i = 0; i <= 10; i++) {
        const x = chartLeft + (chartW / 10) * i;
        ctx.moveTo(x, chartTop);
        ctx.lineTo(x, chartBottom);
      }
      ctx.strokeStyle = 'rgba(255,255,255,0.03)';
      ctx.lineWidth = 1;
      ctx.stroke();

      
      ctx.fillStyle = 'rgba(255,255,255,0.12)';
      ctx.font = '10px monospace';
      ctx.textAlign = 'right';
      for (let i = 0; i <= 5; i++) {
        const val = allLow + (range / 5) * (5 - i);
        const y = chartTop + (chartH / 5) * i;
        ctx.fillText('$' + val.toFixed(0), chartRight + 50, y + 3);
      }

      
      for (let i = 0; i < shown; i++) {
        const c = candles[i];
        const x = chartLeft + i * gap + gap / 2;
        const fadeIn = i >= shown - 2 ? Math.min(1, revealIndex - i) : 1;
        const isGreen = c.bullish;
        const r = isGreen ? 56 : 255;
        const g = isGreen ? 254 : 82;
        const b = isGreen ? 96 : 82;

        const bodyTop = toY(Math.max(c.open, c.close));
        const bodyBot = toY(Math.min(c.open, c.close));
        const bodyH = Math.max(bodyBot - bodyTop, 1);

        
        ctx.beginPath();
        ctx.moveTo(x, toY(c.high));
        ctx.lineTo(x, toY(c.low));
        ctx.strokeStyle = `rgba(${r},${g},${b},${fadeIn * 0.7})`;
        ctx.lineWidth = 1;
        ctx.stroke();

        
        ctx.fillStyle = `rgba(${r},${g},${b},${fadeIn})`;
        ctx.fillRect(x - candleW / 2, bodyTop, candleW, bodyH);

        
        if (i >= shown - 3) {
          ctx.fillStyle = `rgba(${r},${g},${b},${fadeIn * 0.15})`;
          ctx.fillRect(x - candleW / 2 - 4, bodyTop - 4, candleW + 8, bodyH + 8);
        }
      }

      
      if (shown > 5) {
        ctx.beginPath();
        for (let i = 4; i < shown; i++) {
          let sum = 0;
          for (let j = i - 4; j <= i; j++) sum += candles[j].close;
          const x = chartLeft + i * gap + gap / 2;
          const y = toY(sum / 5);
          if (i === 4) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.strokeStyle = 'rgba(56,254,96,0.3)';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        if (shown > 10) {
          ctx.beginPath();
          for (let i = 9; i < shown; i++) {
            let sum = 0;
            for (let j = i - 9; j <= i; j++) sum += candles[j].close;
            const x = chartLeft + i * gap + gap / 2;
            const y = toY(sum / 10);
            if (i === 9) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          }
          ctx.strokeStyle = 'rgba(168,85,247,0.25)';
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      }

      
      const volTop = h * 0.75;
      const volH = h * 0.08;
      for (let i = 0; i < shown; i++) {
        const c = candles[i];
        const x = chartLeft + i * gap + gap / 2;
        const vol = volumes[i];
        const fadeIn = i >= shown - 2 ? Math.min(1, revealIndex - i) : 1;
        ctx.fillStyle = c.bullish
          ? `rgba(56,254,96,${0.15 * fadeIn})`
          : `rgba(255,82,82,${0.15 * fadeIn})`;
        ctx.fillRect(x - candleW / 2, volTop + volH * (1 - vol), candleW, volH * vol);
      }

      
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = w;
        if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h;
        if (p.y > h) p.y = 0;
        const alpha = 0.2 + 0.2 * Math.sin(time * 2 + p.phase);
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(56,254,96,${alpha})`;
        ctx.fill();
      }

      
      const scanY = chartTop + ((time * 60) % chartH);
      const scanGrad = ctx.createLinearGradient(0, scanY - 20, 0, scanY + 20);
      scanGrad.addColorStop(0, 'rgba(56,254,96,0)');
      scanGrad.addColorStop(0.5, 'rgba(56,254,96,0.06)');
      scanGrad.addColorStop(1, 'rgba(56,254,96,0)');
      ctx.fillStyle = scanGrad;
      ctx.fillRect(chartLeft, scanY - 20, chartW, 40);

      animId = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <div
      className={`fixed inset-0 z-[9999] ${fadeOut ? 'opacity-0' : 'opacity-100'}`}
      style={{ background: '#0a0a0c', transition: 'opacity 0.7s ease' }}
    >
            <canvas ref={canvasRef} className="absolute inset-0" />

            <div className="absolute inset-0 z-[1]" style={{
        backdropFilter: 'blur(1.5px)',
        WebkitBackdropFilter: 'blur(1.5px)',
        background: 'radial-gradient(ellipse at center, rgba(10,10,12,0.15) 0%, rgba(10,10,12,0.4) 50%, rgba(10,10,12,0.7) 100%)',
        pointerEvents: 'none',
      }} />

            <div className="absolute inset-0 flex flex-col items-center justify-center z-[2]">
                <div className="flex flex-col items-center px-16 py-12 rounded-2xl" style={{
          background: 'radial-gradient(ellipse at center, rgba(5,5,7,0.95) 0%, rgba(8,8,10,0.8) 50%, transparent 100%)',
        }}>
                    <div className="mb-10 relative">
                        <div className="absolute inset-0 -m-8 rounded-full" style={{
              background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.6) 40%, transparent 70%)',
            }} />
            <img
              src="/logo.png"
              alt="KiteKraken.Ai"
              className="h-16 md:h-20 w-auto object-contain relative z-10"
              style={{
                filter: 'drop-shadow(0 0 20px rgba(56,254,96,0.5)) drop-shadow(0 0 40px rgba(56,254,96,0.2)) drop-shadow(0 2px 8px rgba(0,0,0,0.9))',
              }}
            />
          </div>

                    <div className="w-72 md:w-96 mb-5">
            <div className="h-[2px] rounded-full bg-[#1a1a1e] overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${progress}%`,
                  background: 'linear-gradient(90deg, #38FE60, #2ee055)',
                  boxShadow: '0 0 15px rgba(56,254,96,0.5), 0 0 30px rgba(56,254,96,0.2)',
                  transition: 'width 0.15s linear',
                }}
              />
            </div>
          </div>

                    <div className="flex items-center gap-3">
            <div className="w-1.5 h-1.5 rounded-full bg-[#38FE60] animate-pulse" style={{ boxShadow: '0 0 6px rgba(56,254,96,0.6)' }} />
            <span className="text-[#999] text-[11px]" style={{
              fontFamily: "'Lato', sans-serif",
              textShadow: '0 0 8px rgba(255,255,255,0.15)',
            }}>{status}</span>
            <span className="text-[#38FE60] text-[11px] font-bold" style={{
              fontFamily: "'Lato', sans-serif",
              textShadow: '0 0 10px rgba(56,254,96,0.5)',
            }}>{progress}%</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadingScreen;
